import { useLayoutEffect, useRef, useState } from 'react';
import { keySignature } from '@/music/notation/keysig';
import { systemWidthAt, type SystemGeometry } from '@/music/notation/lines';
import {
  SCORE_CLEF_SPACES,
  SCORE_DESKTOP_MARGIN_SPACES,
  SCORE_MOBILE_MARGIN_SPACES,
  scoreKeySignatureWidth,
} from './scoreLayout';

const SCORE_HORIZONTAL_OVERHEAD = 16;
const FALLBACK_SCORE_SPACE_SIZE = 10.5;
const MOBILE_WORKSPACE_MAX_WIDTH = 700;

/**
 * The ONE way notation is sized (see `state/displayScale.ts`).
 *
 * `--score-space-size` — px per staff space, set from the user's score scale
 * — is read off the surface, and the system width is derived from the
 * container so a line always fills the canvas. Score Studio used to size
 * itself the opposite way (a fixed system width in staff spaces, the SVG
 * shrinking to fit), which made the same setting mean different things on
 * different pages; it now goes through here too.
 *
 * `scale` is the percentage, passed only so the effect re-measures when the
 * user moves the slider — the value itself arrives via the CSS variable.
 *
 * `lineGeometry` is the system width as a function of size (lines.ts), the
 * same formula as `systemWidth`, so bars per line can work out how small the
 * staff must draw for its lines to fit; `baseSpaceSize` is the device's 100%
 * size (`--score-space-base`), what the fit's floor is a share of. Until the
 * surface is measured there is no geometry, and nothing is fitted.
 */
export function useNotationMetrics(scale: number, key?: string, surfaceKey?: unknown) {
  // Mutable so a page that also needs its own ref on the same element can
  // merge the two with a callback ref.
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [metrics, setMetrics] = useState<{
    spaceSize: number;
    systemWidth: number;
    horizontalMarginSpaces: number;
    horizontalEndMarginSpaces: number;
    lineGeometry?: SystemGeometry;
    baseSpaceSize: number;
  }>(() => ({
    spaceSize: FALLBACK_SCORE_SPACE_SIZE,
    systemWidth: 90,
    horizontalMarginSpaces: SCORE_DESKTOP_MARGIN_SPACES,
    horizontalEndMarginSpaces: SCORE_DESKTOP_MARGIN_SPACES,
    baseSpaceSize: FALLBACK_SCORE_SPACE_SIZE,
  }));

  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    const update = () => {
      const computed = window.getComputedStyle(surface);
      const inheritedSize = Number.parseFloat(computed.getPropertyValue('--score-space-size'));
      const spaceSize = Number.isFinite(inheritedSize) ? inheritedSize : FALLBACK_SCORE_SPACE_SIZE;
      const width = surface.clientWidth || window.innerWidth;
      const isMobileWorkspace = width <= MOBILE_WORKSPACE_MAX_WIDTH;
      const defaultMarginSpaces = isMobileWorkspace
        ? SCORE_MOBILE_MARGIN_SPACES
        : SCORE_DESKTOP_MARGIN_SPACES;
      const leadingInsetPx = Number.parseFloat(computed.getPropertyValue('--score-leading-gutter'));
      const trailingInsetPx = Number.parseFloat(
        computed.getPropertyValue('--score-trailing-gutter'),
      );
      const usesEmbeddedGutters =
        Number.isFinite(leadingInsetPx) && Number.isFinite(trailingInsetPx);
      const horizontalMarginSpaces = usesEmbeddedGutters
        ? leadingInsetPx / spaceSize
        : defaultMarginSpaces;
      const horizontalEndMarginSpaces = usesEmbeddedGutters
        ? trailingInsetPx / spaceSize
        : defaultMarginSpaces;
      const keySignatureWidth = scoreKeySignatureWidth(keySignature(key).length);
      // The overhead in two parts: px gutters come off the surface, while the
      // margins, clef and key signature are staff spaces and scale with it.
      const geometry: SystemGeometry = usesEmbeddedGutters
        ? {
            px: width - leadingInsetPx - trailingInsetPx,
            spaces: SCORE_CLEF_SPACES + keySignatureWidth,
          }
        : isMobileWorkspace
          ? {
              px: width,
              spaces: 2 * SCORE_MOBILE_MARGIN_SPACES + SCORE_CLEF_SPACES + keySignatureWidth,
            }
          : { px: width, spaces: SCORE_HORIZONTAL_OVERHEAD };
      const systemWidth = systemWidthAt(geometry, spaceSize);
      const inheritedBase = Number.parseFloat(computed.getPropertyValue('--score-space-base'));
      const baseSpaceSize =
        Number.isFinite(inheritedBase) && inheritedBase > 0 ? inheritedBase : spaceSize;
      // Bail without calling setState when nothing moved: this runs from a
      // ResizeObserver during resize storms, and a fresh object every call
      // schedules real updates that can compound into React's nested-update
      // limit (the page-view pagers hit exactly that — a blank screen).
      setMetrics((prev) =>
        prev.spaceSize === spaceSize &&
        prev.systemWidth === systemWidth &&
        prev.horizontalMarginSpaces === horizontalMarginSpaces &&
        prev.horizontalEndMarginSpaces === horizontalEndMarginSpaces &&
        prev.lineGeometry?.px === geometry.px &&
        prev.lineGeometry?.spaces === geometry.spaces &&
        prev.baseSpaceSize === baseSpaceSize
          ? prev
          : {
              spaceSize,
              systemWidth,
              horizontalMarginSpaces,
              horizontalEndMarginSpaces,
              lineGeometry: geometry,
              baseSpaceSize,
            },
      );
    };
    update();
    // A one-off measurement is enough where ResizeObserver is unavailable;
    // the surface just stops tracking later container changes.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(surface);
    return () => observer.disconnect();
  }, [scale, key, surfaceKey]);

  return { surfaceRef, ...metrics };
}
